import { SHAPEFILE_DETAILS_LABEL_LAYER } from "constants/mapLayers.constants";

import { IEditableProjectLayer } from "components/project/layers/hooks";
import { Shapefile } from "components/project/shapefiles/queries";
import { shapefileLayerType } from "components/project/shapefiles/shapefiles.constants";

import { isLineGeometry } from "../../../../utils/geometryUtils";
import { getUncheckedFeatures } from "../../getUncheckedFeatures";

export const SHAPEFILE_DETAIL_DEFAULT_MIN_ZOOM = 4;
export const SHAPEFILE_DETAIL_DEFAULT_MAX_ZOOM = 22;

interface AddShapefilePropertyLayerParams {
  addLayer;
  checkedLayerKeys: string[];
  layerList: IEditableProjectLayer[];
  layerZoomSettings?: Record<string, { minzoom?: number; maxzoom?: number }>;
  shapefile: Shapefile;
  shapefileFeatureProperty: string;
  shapefileFeatures;
  shapefileId: string;
  mapbox: mapboxgl.Map;
}

export function addShapefilePropertyLayer(params: AddShapefilePropertyLayerParams) {
  const {
    addLayer,
    checkedLayerKeys,
    layerZoomSettings,
    shapefile,
    shapefileFeatureProperty,
    shapefileFeatures,
    shapefileId,
    mapbox
  } = params;

  const layerIdBase = shapefile.projectShapefileId || shapefileId;
  const actualShapefileId = shapefile.shapefileId; // The real shapefileId for source-layer

  const zoomSettings = layerZoomSettings?.[SHAPEFILE_DETAILS_LABEL_LAYER];
  const minzoom = zoomSettings?.minzoom ?? SHAPEFILE_DETAIL_DEFAULT_MIN_ZOOM;
  const maxzoom = zoomSettings?.maxzoom ?? SHAPEFILE_DETAIL_DEFAULT_MAX_ZOOM;

  const source = mapbox.getSource(layerIdBase);
  const isShapetile = source && (source as mapboxgl.VectorSource).type === "vector";

  const geomKeys =
    shapefileFeatures?.[actualShapefileId]?.features
      ?.filter((f) => f.checked)
      .map((f) => f.shapefileGeomId) || [];

  const uncheckedFeatures = geomKeys?.length
    ? geomKeys
    : getUncheckedFeatures(actualShapefileId, shapefileFeatures, shapefile);

  const isDetailsLabelChecked = checkedLayerKeys.includes(SHAPEFILE_DETAILS_LABEL_LAYER);
  const textOpacity = isDetailsLabelChecked
    ? ["match", ["get", "shapefileGeomId"], uncheckedFeatures, 1, 0]
    : 0;

  // Detect line geometry from existing features or map layers
  const hasLineGeom = (shapefile?.featureCollection?.features ?? []).some((f) =>
    isLineGeometry(f.geometry?.type)
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const labelLayout: Record<string, unknown> = {
    "text-field": ["get", shapefileFeatureProperty],
    "text-size": 9,
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-padding": 4,
    visibility: isDetailsLabelChecked ? "visible" : "none"
  };
  if (hasLineGeom) {
    labelLayout["symbol-placement"] = "line";
  } else {
    labelLayout["text-variable-anchor"] = ["top", "bottom", "left", "right"];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layerConfig: any = {
    id: `${layerIdBase}${shapefileLayerType.property}`,
    type: "symbol",
    source: layerIdBase,
    layout: labelLayout,
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 2,
      "text-opacity": textOpacity
    },
    minzoom,
    maxzoom
  };
  if (isShapetile) {
    layerConfig["source-layer"] = actualShapefileId;
  }
  addLayer(layerConfig);
}
